import axiosInstance from './axiosConfig';

/** Default built-in platform list (fallback if admin connect-platforms API is unavailable). Custom platforms come from the registry API. */
export const SOCIAL_CONNECT_PLATFORMS = ['YOUTUBE', 'INSTAGRAM', 'FACEBOOK', 'TIKTOK', 'LINKEDIN'] as const;
export type SocialConnectPlatform = (typeof SOCIAL_CONNECT_PLATFORMS)[number];

export const SOCIAL_PLATFORM_LABELS: Record<SocialConnectPlatform, string> = {
    YOUTUBE: 'YouTube',
    INSTAGRAM: 'Instagram',
    FACEBOOK: 'Facebook',
    TIKTOK: 'TikTok',
    LINKEDIN: 'LinkedIn'
};

/** Row from GET /admin/connect-platforms (built-in + custom registry). */
export interface SocialConnectPlatformRow {
    platformCode: string;
    displayLabel: string;
    providerKey: string;
    builtin: boolean;
}

export function platformDisplayLabel(platformCode: string): string {
    if ((SOCIAL_PLATFORM_LABELS as Record<string, string>)[platformCode]) {
        return (SOCIAL_PLATFORM_LABELS as Record<string, string>)[platformCode];
    }
    return platformCode;
}

export interface SocialMediaContent {
    id: number;
    title: string;
    description: string;
    contentType: 'VIDEO' | 'IMAGE' | 'LINK';
    platform: string;
    mediaUrl: string;
    thumbnailUrl?: string;
    externalLink?: string;
    tags?: string;
    targetAudience?: string;
    campaignId?: string;
    publishDate?: string;
    expiryDate?: string;
    status: 'DRAFT' | 'PENDING_APPROVAL' | 'APPROVED' | 'PUBLISHED' | 'REJECTED' | 'PAUSED' | 'STOPPED';
    createdAt: string;
    updatedAt: string;
    createdBy: number;
    approvedBy?: number;
}

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
}

export interface SocialMediaAnalyticsOverview {
    totalContent: number;
    activeCampaigns: number;
    scheduledCampaigns: number;
    expiredCampaigns: number;
    statusCounts: Record<string, number>;
    platformCounts: Record<string, number>;
    campaignBreakdown: Record<string, Record<string, number>>;
    dailyTrend: Array<{
        date: string;
        created: number;
        approved: number;
        running: number;
        paused: number;
        stopped: number;
    }>;
    conversionFunnel: Array<{
        stage: string;
        count: number;
    }>;
    generatedAt: string;
}

export interface SocialOAuthConnection {
    id: number;
    platform: string;
    accountId?: string;
    accountName?: string;
    status: 'CONNECTED' | 'DISCONNECTED' | 'ERROR';
    tokenExpiresAt?: string;
    connectedAt?: string;
    lastError?: string;
}

export interface SocialPublishState {
    id: number;
    platform: string;
    status: 'QUEUED' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'RETRYING' | 'PAUSED' | 'STOPPED';
    externalPostId?: string;
    lastPublishAt?: string;
    lastError?: string;
    retryCount?: number;
}

export interface SocialPublishAttemptLog {
    id: number;
    platform: string;
    status: string;
    attemptNumber: number;
    errorMessage?: string;
    createdAt: string;
}

export interface SocialPublishStatusResponse {
    jobs: any[];
    states: SocialPublishState[];
    attempts: SocialPublishAttemptLog[];
}

class SocialMediaContentService {
    // Update base URLs - both admin and retail use the same endpoints
    private getBaseUrl() {
        // Both admin and retail users use the same social media endpoints
        const baseUrl = '/api/v1/social-media';
        console.log(`[Service] Using social media endpoint: ${baseUrl}`);
        return baseUrl;
    }

    async getAllContent(): Promise<ApiResponse<SocialMediaContent[]>> {
        try {
            console.log('[Service] Getting all content');
            const response = await axiosInstance.get(`${this.getBaseUrl()}/content`);
            console.log('[Service] Content response:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error fetching content:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch content'
            };
        }
    }

    async getActiveContent(): Promise<ApiResponse<SocialMediaContent[]>> {
        try {
            console.log('[Service] Getting active content');
            const response = await axiosInstance.get(`${this.getBaseUrl()}/content/active`);
            console.log('[Service] Active content response:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error fetching active content:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch active content'
            };
        }
    }

    async createContent(data: Partial<SocialMediaContent>): Promise<ApiResponse<SocialMediaContent>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/content`, data);
            console.log('[Service] Content created:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error creating content:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to create content'
            };
        }
    }

    async uploadAsset(file: File): Promise<ApiResponse<string>> {
        try {
            const form = new FormData();
            form.append('file', file);
            const response = await axiosInstance.post(`${this.getBaseUrl()}/content/upload-asset`, form, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            const body = response.data;
            const url = body?.url ?? body?.data?.url ?? body?.mediaUrl ?? body?.data?.mediaUrl;
            if (!url) {
                return { success: false, error: 'Upload succeeded but no URL was returned' };
            }
            return { success: true, data: url };
        } catch (error: any) {
            console.error('[Service] Error uploading social media asset:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to upload asset'
            };
        }
    }

    async updateContent(id: number, data: Partial<SocialMediaContent>): Promise<ApiResponse<SocialMediaContent>> {
        try {
            const response = await axiosInstance.put(`${this.getBaseUrl()}/content/${id}`, data);
            console.log('[Service] Content updated:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error updating content:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to update content'
            };
        }
    }

    async deleteContent(id: number): Promise<ApiResponse<void>> {
        try {
            await axiosInstance.delete(`${this.getBaseUrl()}/content/${id}`);
            console.log('[Service] Content deleted');
            return { success: true };
        } catch (error: any) {
            console.error('[Service] Error deleting content:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to delete content'
            };
        }
    }

    async submitForApproval(id: number): Promise<ApiResponse<SocialMediaContent>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/content/${id}/submit`);
            console.log('[Service] Content submitted for approval:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error submitting content for approval:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to submit content for approval'
            };
        }
    }

    async approveContent(id: number): Promise<ApiResponse<SocialMediaContent>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/content/${id}/approve`);
            console.log('[Service] Content approved:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error approving content:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to approve content'
            };
        }
    }

    async rejectContent(id: number, reason: string): Promise<ApiResponse<SocialMediaContent>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/content/${id}/reject`, { reason });
            console.log('[Service] Content rejected:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error rejecting content:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to reject content'
            };
        }
    }

    async publishContent(id: number): Promise<ApiResponse<SocialMediaContent>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/content/${id}/publish`);
            console.log('[Service] Content published:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error publishing content:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to publish content'
            };
        }
    }

    async getAnalyticsOverview(filters?: {
        days?: number;
        fromDate?: string;
        toDate?: string;
        platform?: string;
    }): Promise<ApiResponse<SocialMediaAnalyticsOverview>> {
        try {
            const params = new URLSearchParams();
            if (filters?.days) params.append('days', String(filters.days));
            if (filters?.fromDate) params.append('fromDate', filters.fromDate);
            if (filters?.toDate) params.append('toDate', filters.toDate);
            if (filters?.platform && filters.platform !== 'all') params.append('platform', filters.platform);
            const query = params.toString() ? `?${params.toString()}` : '';
            const response = await axiosInstance.get(`${this.getBaseUrl()}/analytics/overview${query}`);
            console.log('[Service] Analytics overview response:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error fetching analytics overview:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to fetch analytics overview'
            };
        }
    }

    async startCampaign(id: number): Promise<ApiResponse<SocialMediaContent>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/content/${id}/start`);
            console.log('[Service] Campaign started:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error starting campaign:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to start campaign'
            };
        }
    }

    async pauseCampaign(id: number): Promise<ApiResponse<SocialMediaContent>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/content/${id}/pause`);
            console.log('[Service] Campaign paused:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error pausing campaign:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to pause campaign'
            };
        }
    }

    async stopCampaign(id: number): Promise<ApiResponse<SocialMediaContent>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/content/${id}/stop`);
            console.log('[Service] Campaign stopped:', response.data);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            console.error('[Service] Error stopping campaign:', error.response || error);
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to stop campaign'
            };
        }
    }

    async listConnectPlatforms(): Promise<ApiResponse<SocialConnectPlatformRow[]>> {
        try {
            const response = await axiosInstance.get(`${this.getBaseUrl()}/admin/connect-platforms`);
            const raw = response.data?.data ?? response.data;
            const list = Array.isArray(raw) ? raw : [];
            return { success: true, data: list as SocialConnectPlatformRow[] };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to load connect platforms'
            };
        }
    }

    async registerConnectPlatform(payload: {
        platformCode: string;
        displayLabel: string;
        providerKey: string;
    }): Promise<ApiResponse<unknown>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/admin/connect-platforms`, payload);
            return { success: true, data: response.data?.data ?? response.data };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to register platform'
            };
        }
    }

    async removeConnectPlatform(platformCode: string): Promise<ApiResponse<void>> {
        try {
            await axiosInstance.delete(
                `${this.getBaseUrl()}/admin/connect-platforms/${encodeURIComponent(platformCode)}`
            );
            return { success: true };
        } catch (error: any) {
            return {
                success: false,
                error: error.response?.data?.message || error.message || 'Failed to remove platform'
            };
        }
    }

    async connectPlatform(platform: string): Promise<ApiResponse<{ authorizationUrl: string }>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/oauth/${encodeURIComponent(platform)}/connect`);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            return { success: false, error: error.response?.data?.message || error.message || 'Failed to start OAuth connect flow' };
        }
    }

    async getConnections(): Promise<ApiResponse<SocialOAuthConnection[]>> {
        try {
            const response = await axiosInstance.get(`${this.getBaseUrl()}/oauth/connections`);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            return { success: false, error: error.response?.data?.message || error.message || 'Failed to load social connections' };
        }
    }

    async disconnectPlatform(platform: string): Promise<ApiResponse<void>> {
        try {
            await axiosInstance.delete(`${this.getBaseUrl()}/oauth/${encodeURIComponent(platform)}/disconnect`);
            return { success: true };
        } catch (error: any) {
            return { success: false, error: error.response?.data?.message || error.message || 'Failed to disconnect platform' };
        }
    }

    async getPublishStatus(contentId: number): Promise<ApiResponse<SocialPublishStatusResponse>> {
        try {
            const response = await axiosInstance.get(`${this.getBaseUrl()}/content/${contentId}/publish-status`);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            return { success: false, error: error.response?.data?.message || error.message || 'Failed to load publish status' };
        }
    }

    async retryPublishJob(jobId: number): Promise<ApiResponse<{ status: string; jobId: number }>> {
        try {
            const response = await axiosInstance.post(`${this.getBaseUrl()}/jobs/${jobId}/retry`);
            return { success: true, data: response.data.data || response.data };
        } catch (error: any) {
            return { success: false, error: error.response?.data?.message || error.message || 'Failed to retry publish job' };
        }
    }

    async getPlatformPublishMode(platform: string): Promise<ApiResponse<'simulated' | 'live'>> {
        try {
            const response = await axiosInstance.get(
                `/api/admin/providers/social/${encodeURIComponent(platform.toLowerCase())}/publish-mode`
            );
            const mode = response.data?.publishMode || response.data?.data?.publishMode || 'simulated';
            return { success: true, data: mode };
        } catch (error: any) {
            return { success: false, error: error.response?.data?.message || error.message || 'Failed to load platform publish mode' };
        }
    }

    async setPlatformPublishMode(platform: string, publishMode: 'simulated' | 'live'): Promise<ApiResponse<void>> {
        try {
            await axiosInstance.put(
                `/api/admin/providers/social/${encodeURIComponent(platform.toLowerCase())}/publish-mode`,
                { publishMode }
            );
            return { success: true };
        } catch (error: any) {
            return { success: false, error: error.response?.data?.message || error.message || 'Failed to update platform publish mode' };
        }
    }
}

export const socialMediaContentService = new SocialMediaContentService();
export default socialMediaContentService; 